import React, { useState, useEffect, useMemo } from 'react';
import {
  PlusCircle,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  MessageSquare,
  ArrowRight,
  MapPin,
  Sparkles,
  Eye,
  CreditCard,
  User,
  Bell,
  HelpCircle,
  LogOut,
  Search,
  Filter,
  Check,
  Phone,
  ShieldCheck,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  DollarSign,
  AlertTriangle,
  Send,
  Star,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import { JobDetailsModal } from '../../components/jobs/JobDetailsModal';
import { ShareJobButton } from '../../components/common/ShareJobButton';
import { CustomerProfileModal } from '../../components/customer/CustomerProfileModal';
import { JobRatingModal } from '../../components/jobs/JobRatingModal';
import { RealTimeJobStatusTracker } from '../../components/jobs/RealTimeJobStatusTracker';
import { StarRating } from '../../components/common/StarRating';
import { Job, Quote } from '../../types';
import { TelegramChatButton } from '../../components/TelegramChatButton';
import { PushNotificationSettingsCard } from '../../components/notifications/PushNotificationSettingsCard';
import {
  confirmJobWorkCompletionApi,
  releaseJobPaymentRpc,
  fetchTemporaryCompletionPhoto,
  discardTemporaryCompletionPhoto,
  initializeMarketplacePayment,
  checkJobPaymentStatus,
  fetchCustomerNotificationPreferencesApi,
  updateCustomerNotificationPreferencesApi,
  cleanContactPhone,
  CustomerNotificationPreferences,
} from '../../lib/supabaseService';

export type CustomerDashboardTab =
  | 'overview'
  | 'jobs'
  | 'active'
  | 'completed'
  | 'quotes'
  | 'payments'
  | 'messages'
  | 'notifications'
  | 'profile'
  | 'help';

export const CustomerDashboard: React.FC = () => {
  const { currentUser, jobs, quotes, profiles, notifications, markNotificationAsRead, navigateTo, logoutUser, viewParams, refreshJobsAndQuotes } = useApp();

  const [activeTab, setActiveTab] = useState<CustomerDashboardTab>('overview');
  const [activeSubFilter, setActiveSubFilter] = useState<'all' | 'in_progress' | 'awaiting'>('all');
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<Job | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [confirmingJobId, setConfirmingJobId] = useState<string | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [isConfirmingCompletion, setIsConfirmingCompletion] = useState(false);
  const [completionFeedback, setCompletionFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tempModalPhoto, setTempModalPhoto] = useState<string | null>(null);
  const [ratingJob, setRatingJob] = useState<Job | null>(null);

  // Sync tab and refresh jobs upon payment redirect / viewParams change
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const paymentSuccess = viewParams.paymentStatus === 'success' || params.get('payment') === 'success';
    const hired = params.get('hired') === 'true';

    if (viewParams.tab === 'active' || viewParams.activeTab === 'active' || viewParams.adminTab === 'active' || tabParam === 'active' || paymentSuccess || hired) {
      setActiveTab('active');
    } else if (viewParams.tab && ['overview', 'jobs', 'active', 'completed', 'quotes', 'payments', 'messages', 'notifications', 'profile', 'help'].includes(viewParams.tab)) {
      setActiveTab(viewParams.tab as CustomerDashboardTab);
    } else if (tabParam && ['overview', 'jobs', 'active', 'completed', 'quotes', 'payments', 'messages', 'notifications', 'profile', 'help'].includes(tabParam)) {
      setActiveTab(tabParam as CustomerDashboardTab);
    }

    if (paymentSuccess || hired || viewParams.paystackRef) {
      refreshJobsAndQuotes();
    }

    const targetJobId = viewParams.jobId || params.get('jobId') || params.get('id');
    if (targetJobId && jobs.length > 0 && !selectedJobForDetails) {
      const matched = jobs.find((j) => j.id === targetJobId);
      if (matched) {
        setSelectedJobForDetails(matched);
      }
    }
  }, [viewParams, refreshJobsAndQuotes, jobs, selectedJobForDetails]);

  // Real-time job status tracking auto-sync interval
  useEffect(() => {
    let pollTimer: any = null;
    if (activeTab === 'active' || activeTab === 'completed' || activeTab === 'jobs' || activeTab === 'overview') {
      pollTimer = setInterval(() => {
        refreshJobsAndQuotes();
      }, 10000);
    }
    return () => {
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [activeTab, refreshJobsAndQuotes]);

  // Search and filters for My Jobs tab
  const [jobSearch, setJobSearch] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState<'all' | 'open' | 'in_progress' | 'completed_by_professional' | 'completed'>('all');

  // Payment processing state
  const [initiatingPayJobId, setInitiatingPayJobId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Logout confirmation modal state
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Dynamic payment status and completion photo evidence tracking
  const [paymentStatusesByJobId, setPaymentStatusesByJobId] = useState<Record<string, { isPaid: boolean; paymentStatus: string }>>({});
  const [completionPhotosByJobId, setCompletionPhotosByJobId] = useState<Record<string, string>>({});
  const [releasedJobIds, setReleasedJobIds] = useState<Set<string>>(new Set());
  const [hasAuthorizedRelease, setHasAuthorizedRelease] = useState(false);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [previewFullscreenPhoto, setPreviewFullscreenPhoto] = useState<string | null>(null);

  // Customer Notification Preferences State
  const [notifPrefs, setNotifPrefs] = useState<CustomerNotificationPreferences | null>(null);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [notifPrefsSavedMsg, setNotifPrefsSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser?.id) {
      fetchCustomerNotificationPreferencesApi(currentUser.id)
        .then((res) => {
          if (res?.preferences) setNotifPrefs(res.preferences);
        })
        .catch(() => {});
    }
  }, [currentUser?.id]);

  const handleToggleNotifPref = async (key: keyof CustomerNotificationPreferences) => {
    if (!currentUser?.id || !notifPrefs) return;
    const nextVal = !Boolean(notifPrefs[key]);
    const updated = { ...notifPrefs, [key]: nextVal };
    setNotifPrefs(updated);
    setSavingNotifPrefs(true);
    setNotifPrefsSavedMsg(null);
    try {
      const res = await updateCustomerNotificationPreferencesApi(currentUser.id, { [key]: nextVal });
      if (res?.preferences) setNotifPrefs(res.preferences);
      setNotifPrefsSavedMsg('Notification preferences saved');
      setTimeout(() => setNotifPrefsSavedMsg(null), 3000);
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  // Helper to determine if payment has already been released for a job
  const isPaymentReleased = (job: Job) => {
    if (releasedJobIds.has(job.id)) {
      return true;
    }
    if (job.payment_released || (job as any).paymentReleased || (job as any).payment_released === true) {
      return true;
    }
    const status = String(job.status || '').toLowerCase();
    if (status === 'completed' && (job.payment_released || (job as any).payment_status === 'released')) {
      return true;
    }
    const directPaymentStatus = String((job as any).payment_status || (job as any).paymentStatus || '').toLowerCase();
    if (directPaymentStatus === 'released') {
      return true;
    }
    const apiStatus = paymentStatusesByJobId[job.id];
    if (apiStatus && apiStatus.paymentStatus === 'released') {
      return true;
    }
    return false;
  };

  // Load completion photo evidence when Payment Release Confirmation modal is opened
  useEffect(() => {
    if (confirmingJobId) {
      setHasAuthorizedRelease(false);
      setEvidenceError(null);
      setLoadingEvidence(true);
      fetchTemporaryCompletionPhoto(confirmingJobId)
        .then((url) => {
          if (url) {
            setTempModalPhoto(url);
            setCompletionPhotosByJobId((prev) => ({ ...prev, [confirmingJobId]: url }));
          }
        })
        .catch((err) => {
          console.warn('Error fetching completion photo evidence:', err);
          setEvidenceError('Unable to load photo preview from server.');
        })
        .finally(() => {
          setLoadingEvidence(false);
        });
    } else {
      setTempModalPhoto(null);
      setHasAuthorizedRelease(false);
      setLoadingEvidence(false);
      setEvidenceError(null);
      setPreviewFullscreenPhoto(null);
    }
  }, [confirmingJobId]);

  // Filter jobs belonging to current customer (supporting customerId, customer_id, clean phone, and email)
  const customerJobs = useMemo(() => {
    return jobs.filter((j) => {
      const jCustId = j.customerId || (j as any).customer_id;
      const isOwner = Boolean(currentUser?.id && jCustId && String(jCustId).toLowerCase() === String(currentUser.id).toLowerCase());
      const isPhoneMatch = Boolean(
        currentUser?.phone &&
        cleanContactPhone(j.customerPhone || (j as any).customer_phone) &&
        cleanContactPhone(j.customerPhone || (j as any).customer_phone) === cleanContactPhone(currentUser.phone)
      );
      const isEmailMatch = Boolean(
        currentUser?.email &&
        ((j as any).customerEmail || (j as any).customer_email) &&
        String((j as any).customerEmail || (j as any).customer_email).toLowerCase().trim() === currentUser.email.toLowerCase().trim()
      );
      return isOwner || isPhoneMatch || isEmailMatch;
    });
  }, [jobs, currentUser?.id, currentUser?.phone, currentUser?.email]);

  // Helper to verify if customer has hired a professional for this job
  const hasHiredProfessional = (j: Job) => {
    return Boolean(
      j.selectedProfessionalId ||
      (j as any).professionalId ||
      (j as any).professional_id ||
      (j as any).selected_professional_id ||
      quotes.some((q) => q.jobId === j.id && q.status === 'accepted')
    );
  };

  // Authoritative helper to determine if a job is fully completed and confirmed
  const isTrulyCompletedJob = (j: Job) => {
    const s = String(j.status || '').toLowerCase();
    const cs = String(j.completionStatus || (j as any).completion_status || '').toLowerCase();
    return Boolean(
      s === 'completed' ||
      cs === 'confirmed' ||
      isPaymentReleased(j) ||
      j.confirmed_by_customer ||
      (j as any).confirmedByCustomer ||
      (j as any).customer_confirmed_at ||
      j.completed_by_customer_at
    );
  };

  const isJobCancelledOrExpired = (j: Job) => {
    const s = String(j.status || '').toLowerCase();
    if (s === 'cancelled' || s === 'expired' || Boolean(j.cancelled_at) || Boolean(j.expired_at)) return true;
    if (s === 'open' && !hasHiredProfessional(j) && j.createdAt) {
      const createdMs = new Date(j.createdAt).getTime();
      if (!isNaN(createdMs) && Date.now() - createdMs >= 24 * 60 * 60 * 1000) return true;
    }
    return false;
  };

  // Helper to check if a job has verified escrow funding from any source
  const isJobFundedInEscrowOrReleased = (j: Job) => {
    if (isPaymentReleased(j) || j.status === 'completed' || j.status === 'in_progress' || j.status === 'completed_by_professional') {
      return true;
    }
    const pState = paymentStatusesByJobId[j.id];
    if (pState?.isPaid) return true;
    const payValues = [
      pState?.paymentStatus,
      j.paymentStatus,
      (j as any).payment_status,
      (j.aiScopeEstimate as any)?.lifecycle_meta?.payment_status,
    ]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase());
    if (payValues.some((v) => v === 'held_in_escrow' || v === 'paid' || v === 'released')) {
      return true;
    }
    if (
      j.escrow_held === true ||
      (j as any).held_in_escrow === true ||
      Boolean(j.payment_reference || (j as any).paymentReference || (j.aiScopeEstimate as any)?.lifecycle_meta?.payment_reference)
    ) {
      return true;
    }
    return false;
  };

  const openJobs = useMemo(
    () =>
      customerJobs.filter(
        (j) =>
          j.status === 'open' &&
          !isJobFundedInEscrowOrReleased(j) &&
          !isJobCancelledOrExpired(j) &&
          !isTrulyCompletedJob(j)
      ),
    [customerJobs, releasedJobIds, paymentStatusesByJobId]
  );

  // Only active jobs for customers who have hired a professional and work is currently in progress (strictly excluding completed, cancelled, and expired jobs)
  const activeJobs = useMemo(
    () =>
      customerJobs.filter(
        (j) =>
          (hasHiredProfessional(j) || isJobFundedInEscrowOrReleased(j)) &&
          !isJobCancelledOrExpired(j) &&
          !isTrulyCompletedJob(j) &&
          (j.status === 'assigned' || j.status === 'in_progress' || isJobFundedInEscrowOrReleased(j)) &&
          !j.completed_by_professional &&
          j.completionStatus !== 'completed_by_professional' &&
          (j as any).completion_status !== 'awaiting_customer_confirmation'
      ),
    [customerJobs, quotes, releasedJobIds, paymentStatusesByJobId]
  );

  // Awaiting customer confirmation: customer hired professional who finished work, awaiting inspection & payment release (strictly excluding completed, cancelled, and expired jobs)
  const awaitingConfirmationJobs = useMemo(
    () =>
      customerJobs.filter(
        (j) =>
          (hasHiredProfessional(j) || isJobFundedInEscrowOrReleased(j)) &&
          !isJobCancelledOrExpired(j) &&
          !isTrulyCompletedJob(j) &&
          (j.status === 'completed_by_professional' ||
            (j.status as string) === 'professional_completed' ||
            j.completionStatus === 'completed_by_professional' ||
            (j as any).completion_status === 'awaiting_customer_confirmation' ||
            Boolean(j.completed_by_professional) ||
            Boolean(j.completed_by_pro_at)) &&
          j.status !== 'cancelled' &&
          j.status !== 'expired'
      ),
    [customerJobs, quotes, releasedJobIds, paymentStatusesByJobId]
  );

  const completedJobs = useMemo(
    () => customerJobs.filter((j) => isTrulyCompletedJob(j)),
    [customerJobs, releasedJobIds, paymentStatusesByJobId]
  );

  // Query payment statuses & completion photos for customer jobs
  useEffect(() => {
    const relevantJobs = customerJobs.filter((j) => j.status !== 'cancelled' && j.status !== 'expired');
    relevantJobs.forEach((job) => {
      if (!paymentStatusesByJobId[job.id]?.isPaid) {
        checkJobPaymentStatus(job.id).then((res) => {
          if (res && res.success) {
            setPaymentStatusesByJobId((prev) => ({
              ...prev,
              [job.id]: { isPaid: Boolean(res.isPaid), paymentStatus: res.paymentStatus || 'none' },
            }));
            if (res.isPaid) {
              const resolvedPaySt = res.paymentStatus === 'released' ? 'released' : 'held_in_escrow';
              if (job.status === 'open' || job.status === 'assigned') {
                job.status = resolvedPaySt === 'released' ? 'completed' : 'in_progress';
              }
              job.paymentStatus = resolvedPaySt;
              (job as any).payment_status = resolvedPaySt;
              job.escrow_held = resolvedPaySt !== 'released';
            }
          }
        });
      }

      if (!job.completionPhotoUrl && !job.completionPhotoPath && !completionPhotosByJobId[job.id]) {
        fetchTemporaryCompletionPhoto(job.id).then((url) => {
          if (url) {
            setCompletionPhotosByJobId((prev) => ({
              ...prev,
              [job.id]: url,
            }));
          }
        });
      }
    });
  }, [customerJobs, awaitingConfirmationJobs, activeJobs]);

  // Evaluates whether the job status is marked completed (by professional or completed status)
  const isJobStatusCompleted = (job: Job) => {
    const s = String(job.status || '').toLowerCase();
    const cs = String(job.completionStatus || '').toLowerCase();
    return (
      s === 'completed' ||
      s === 'completed_by_professional' ||
      s === 'professional_completed' ||
      cs === 'completed' ||
      cs === 'completed_by_professional' ||
      cs === 'confirmed' ||
      Boolean(job.completed_by_professional)
    );
  };

  // Evaluates whether the assigned professional has marked this job completed
  const isJobMarkedCompletedByPro = (job: Job) => {
    return isJobStatusCompleted(job) && job.status !== 'cancelled' && (job.status as string) !== 'disputed' && !job.payment_released;
  };

  // Conditional logic to verify completion photo metadata exists
  const hasJobCompletionPhotoMetadata = (job: Job) => {
    // 1. Direct completion photo URL or path string on job
    if (typeof job.completionPhotoUrl === 'string' && job.completionPhotoUrl.trim().length > 0) return true;
    if (typeof (job as any).completion_photo_url === 'string' && (job as any).completion_photo_url.trim().length > 0) return true;
    if (typeof job.completionPhotoPath === 'string' && job.completionPhotoPath.trim().length > 0) return true;
    if (typeof (job as any).completion_photo_path === 'string' && (job as any).completion_photo_path.trim().length > 0) return true;
    if (typeof (job as any).completionPhoto === 'string' && (job as any).completionPhoto.trim().length > 0) return true;
    if (typeof (job as any).completion_photo === 'string' && (job as any).completion_photo.trim().length > 0) return true;

    // 2. Dynamic cache from server API fetch (/api/marketplace/jobs/:jobId/completion-photo)
    const cachedPhoto = completionPhotosByJobId[job.id];
    if (typeof cachedPhoto === 'string' && cachedPhoto.trim().length > 0) return true;

    // 3. Temporary modal photo state if currently inspecting this job
    if (confirmingJobId === job.id && typeof tempModalPhoto === 'string' && tempModalPhoto.trim().length > 0) return true;

    // 4. Object metadata on job (e.g., completion_photo_metadata or completionPhotoMetadata or metadata)
    const photoMeta =
      (job as any).completion_photo_metadata ||
      (job as any).completionPhotoMetadata ||
      (job as any).completion_metadata ||
      (job as any).completionMetadata ||
      (job as any).metadata?.completion_photo_metadata ||
      (job as any).metadata?.completion_photo;
    if (typeof photoMeta === 'boolean' && photoMeta) return true;
    if (typeof photoMeta === 'string' && photoMeta.trim().length > 0) return true;
    if (photoMeta && typeof photoMeta === 'object') {
      if (photoMeta.url || photoMeta.path || photoMeta.photoData || photoMeta.photoUrl || photoMeta.photo_url || photoMeta.evidence_url || photoMeta.dataUri) return true;
      if (Object.keys(photoMeta).length > 0) return true;
    }
    if ((job as any).metadata?.completion_photo_url || (job as any).metadata?.completionPhotoUrl) return true;

    // 5. Array of photos or media
    if (Array.isArray((job as any).completion_photos) && (job as any).completion_photos.length > 0) return true;
    if (Array.isArray((job as any).completionPhotos) && (job as any).completionPhotos.length > 0) return true;

    // 6. Job media array containing completion photo
    if (Array.isArray((job as any).job_media)) {
      const hasMediaPhoto = (job as any).job_media.some(
        (m: any) =>
          (m.media_type === 'completion_photo' || m.type === 'completion_photo' || m.is_completion) &&
          Boolean(m.media_url || m.url || m.path)
      );
      if (hasMediaPhoto) return true;
    }

    if ((job as any).has_completion_photo === true || (job as any).hasCompletionPhoto === true || (job as any).completion_photo_exists === true) return true;

    return false;
  };

  // Evaluates whether valid completion photo evidence is available for customer inspection
  const hasJobCompletionPhoto = (job: Job) => hasJobCompletionPhotoMetadata(job);

  // Evaluates whether payment was collected and is currently held in escrow (eligible for release)
  const isJobEscrowHeld = (job: Job) => {
    if (job.payment_released || (job as any).paymentReleased) {
      return false;
    }
    const directPaymentStatus = String((job as any).payment_status || (job as any).paymentStatus || '').toLowerCase();
    if (directPaymentStatus === 'released' || directPaymentStatus === 'refunded' || directPaymentStatus === 'unpaid' || directPaymentStatus === 'failed') {
      return false;
    }
    if (directPaymentStatus === 'held_in_escrow') {
      return true;
    }
    if ((job as any).escrow_held === true || (job as any).held_in_escrow === true || (job as any).is_held_in_escrow === true) {
      return true;
    }
    const directIsPaid = (job as any).is_paid === true || (job as any).isPaid === true;
    if (directIsPaid && (directPaymentStatus === 'held_in_escrow' || directPaymentStatus === 'paid' || !directPaymentStatus)) {
      return true;
    }

    const apiStatus = paymentStatusesByJobId[job.id];
    if (apiStatus) {
      if (apiStatus.paymentStatus === 'released' || apiStatus.paymentStatus === 'refunded') {
        return false;
      }
      return (
        apiStatus.isPaid === true &&
        (apiStatus.paymentStatus === 'held_in_escrow' || apiStatus.paymentStatus === 'paid')
      );
    }
    return (
      (job.status === 'assigned' ||
        job.status === 'in_progress' ||
        job.status === 'completed' ||
        job.status === 'completed_by_professional' ||
        job.status === 'professional_completed') &&
      Boolean(job.payment_reference || (job as any).paymentReference || job.selectedProfessionalId) &&
      !job.payment_released &&
      !(job as any).paymentReleased
    );
  };

  // Strict check: only true when job status is 'completed' (or completed by pro), payment is in escrow, and completion photo metadata exists
  const canReleaseJobPayment = (job: Job) => {
    const isCompleted = isJobStatusCompleted(job);
    const inEscrow = isJobEscrowHeld(job);
    const hasPhotoMeta = hasJobCompletionPhotoMetadata(job);
    return isCompleted && inEscrow && hasPhotoMeta;
  };

  // Jobs that have been completed but their escrow has not yet been released
  const unreleasedCompletedJobs = useMemo(
    () =>
      customerJobs.filter(
        (j) =>
          (j.status === 'completed' || (j as any).status === 'completed') &&
          !j.payment_released &&
          isJobEscrowHeld(j)
      ),
    [customerJobs, paymentStatusesByJobId]
  );

  // Unified active jobs list for the Active & In Progress tab:
  // Strictly includes ONLY jobs in progress and jobs awaiting customer confirmation.
  // Completed jobs are NEVER displayed in the Active & In Progress area!
  const activeCardJobs = useMemo(() => {
    const map = new Map<string, Job>();
    awaitingConfirmationJobs.forEach((j) => {
      if (!isTrulyCompletedJob(j)) {
        map.set(j.id, j);
      }
    });
    activeJobs.forEach((j) => {
      if (!isTrulyCompletedJob(j)) {
        map.set(j.id, j);
      }
    });
    return Array.from(map.values());
  }, [awaitingConfirmationJobs, activeJobs, releasedJobIds, paymentStatusesByJobId]);

  // Sub-filtered list for Active & In Progress tab
  const displayedActiveCardJobs = useMemo(() => {
    if (activeSubFilter === 'awaiting') return awaitingConfirmationJobs;
    if (activeSubFilter === 'in_progress') return activeJobs;
    return activeCardJobs;
  }, [activeSubFilter, activeCardJobs, awaitingConfirmationJobs, activeJobs]);

  // Customer quotes received across all jobs
  const customerQuotes = useMemo(() => {
    const jobIds = new Set(customerJobs.map((j) => j.id));
    return quotes.filter((q) => jobIds.has(q.jobId));
  }, [customerJobs, quotes]);

  // Filtered jobs list
  const filteredCustomerJobs = useMemo(() => {
    return customerJobs.filter((job) => {
      const matchesSearch =
        !jobSearch.trim() ||
        job.title.toLowerCase().includes(jobSearch.toLowerCase()) ||
        job.description.toLowerCase().includes(jobSearch.toLowerCase()) ||
        job.lga.toLowerCase().includes(jobSearch.toLowerCase());

      const isComp = isTrulyCompletedJob(job);
      const isAwaiting =
        !isComp &&
        (job.status === 'completed_by_professional' ||
          job.status === 'professional_completed' ||
          job.completionStatus === 'completed_by_professional' ||
          (job as any).completion_status === 'awaiting_customer_confirmation' ||
          Boolean(job.completed_by_professional));

      const isInProg =
        !isComp && !isAwaiting && (job.status === 'assigned' || job.status === 'in_progress');

      const matchesStatus =
        jobStatusFilter === 'all' ||
        (jobStatusFilter === 'open' && job.status === 'open' && !isComp) ||
        (jobStatusFilter === 'in_progress' && isInProg) ||
        (jobStatusFilter === 'completed_by_professional' && isAwaiting) ||
        (jobStatusFilter === 'completed' && isComp);

      return matchesSearch && matchesStatus;
    });
  }, [customerJobs, jobSearch, jobStatusFilter, releasedJobIds, paymentStatusesByJobId]);

  // Notifications for customer
  const customerNotifications = useMemo(() => {
    return notifications.filter((n) => n.userId === currentUser?.id || n.userId === 'all_customers');
  }, [notifications, currentUser?.id]);

  const unreadNotifsCount = customerNotifications.filter((n) => !n.read).length;

  // Handle Confirming Work Completion & Escrow Release
  const handleConfirmWorkCompleted = async (jobId: string) => {
    if (!currentUser?.id) return;
    setIsConfirmingCompletion(true);
    setCompletionFeedback(null);
    try {
      const res = await releaseJobPaymentRpc(jobId, completionNotes, currentUser.id);
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Work confirmed! The 90% net earnings have been safely released to the professional.',
        });
        setConfirmingJobId(null);
        setCompletionNotes('');

        // Mark jobId as released in local state immediately so UI updates instantly!
        setReleasedJobIds((prev) => new Set(prev).add(jobId));

        setPaymentStatusesByJobId((prev) => ({
          ...prev,
          [jobId]: { isPaid: true, paymentStatus: 'released' },
        }));

        // Mutate target job object in memory for instant local component reactivity
        const targetJobObj = jobs.find((j) => j.id === jobId);
        if (targetJobObj) {
          targetJobObj.payment_released = true;
          targetJobObj.status = 'completed';
          (targetJobObj as any).payment_status = 'released';
        }

        // Discard temporary completion photo from local cache and trigger server-side purge to save space
        setCompletionPhotosByJobId((prev) => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
        discardTemporaryCompletionPhoto(jobId).catch(() => {});

        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }

        // Prompt customer to rate artisan immediately after confirmation
        const targetJob = jobs.find((j) => j.id === jobId);
        if (targetJob) {
          setTimeout(() => {
            setRatingJob(targetJob);
          }, 800);
        }

        setTimeout(() => setCompletionFeedback(null), 6000);
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to confirm job completion.',
        });
      }
    } catch (e: any) {
      setCompletionFeedback({
        type: 'error',
        text: e.message || 'Error communicating with server.',
      });
    } finally {
      setIsConfirmingCompletion(false);
    }
  };

  // Handle Paystack Escrow Payment Initiation
  const handleInitiatePayment = async (job: Job, quote?: Quote) => {
    if (!currentUser?.id || !currentUser.email) {
      setPaymentError('Please ensure your account has a valid email before making payment.');
      return;
    }

    // Immediate client-side lock if job is already funded in escrow or released
    if (isJobFundedInEscrowOrReleased(job)) {
      setPaymentStatusesByJobId((prev) => ({
        ...prev,
        [job.id]: { isPaid: true, paymentStatus: isPaymentReleased(job) ? 'released' : 'held_in_escrow' },
      }));
      if (job.status === 'open' || job.status === 'assigned') {
        job.status = 'in_progress';
      }
      job.paymentStatus = isPaymentReleased(job) ? 'released' : 'held_in_escrow';
      (job as any).payment_status = job.paymentStatus;
      job.escrow_held = !isPaymentReleased(job);
      setActiveTab('active');
      setCompletionFeedback({
        type: 'success',
        text: 'Payment for this job is already verified and held in escrow. Your job is in Active Jobs.',
      });
      return;
    }

    setInitiatingPayJobId(job.id);
    setPaymentError(null);

    try {
      const liveCheck = await checkJobPaymentStatus(job.id);
      if (liveCheck.isPaid) {
        setPaymentStatusesByJobId((prev) => ({
          ...prev,
          [job.id]: { isPaid: true, paymentStatus: liveCheck.paymentStatus || 'held_in_escrow' },
        }));
        job.status = liveCheck.paymentStatus === 'released' ? 'completed' : 'in_progress';
        job.paymentStatus = liveCheck.paymentStatus === 'released' ? 'released' : 'held_in_escrow';
        (job as any).payment_status = job.paymentStatus;
        job.escrow_held = liveCheck.paymentStatus !== 'released';
        setActiveTab('active');
        setCompletionFeedback({
          type: 'success',
          text: 'Payment for this job is already verified and held in escrow. Your job is in Active Jobs.',
        });
        if (refreshJobsAndQuotes) {
          refreshJobsAndQuotes();
        }
        return;
      }

      const targetQuote = quote || quotes.find((q) => q.jobId === job.id && q.status === 'accepted') || quotes.find((q) => q.jobId === job.id);
      const grossAmount = targetQuote?.totalAmount || job.budgetEstimate || 10000;

      const res = await initializeMarketplacePayment({
        jobId: job.id,
        quoteId: targetQuote?.id,
        customerId: currentUser.id,
        professionalId: targetQuote?.professionalId || job.selectedProfessionalId || '',
        grossAmount: grossAmount,
        customerEmail: currentUser.email,
        customerName: currentUser.fullName,
        callbackUrl: `${window.location.origin}/api/marketplace/paystack/callback`,
      });

      if (res.success && res.authorization_url) {
        window.location.href = res.authorization_url;
      } else if (res.alreadyPaid) {
        setPaymentStatusesByJobId((prev) => ({
          ...prev,
          [job.id]: { isPaid: true, paymentStatus: 'held_in_escrow' },
        }));
        job.status = 'in_progress';
        job.paymentStatus = 'held_in_escrow';
        (job as any).payment_status = 'held_in_escrow';
        job.escrow_held = true;
        setActiveTab('active');
        setCompletionFeedback({
          type: 'success',
          text: 'Payment for this job is already verified and held in escrow. Moved to Active Jobs.',
        });
        if (refreshJobsAndQuotes) {
          refreshJobsAndQuotes();
        }
      } else {
        setPaymentError(res.error || 'Failed to initialize Paystack payment.');
      }
    } catch (err: any) {
      setPaymentError(err.message || 'Network error initiating Paystack payment.');
    } finally {
      setInitiatingPayJobId(null);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logoutUser();
      navigateTo('home');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200">
      {/* Top Banner / Navigation Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Global Feedback Alert */}
        {completionFeedback && (
          <div
            className={`p-4 rounded-2xl flex items-center justify-between gap-3 text-xs font-bold animate-fade-in ${
              completionFeedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                : 'bg-rose-50 text-rose-900 border border-rose-200'
            }`}
          >
            <div className="flex items-center gap-2">
              {completionFeedback.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              )}
              <span>{completionFeedback.text}</span>
            </div>
            <button
              onClick={() => setCompletionFeedback(null)}
              className="text-slate-400 hover:text-slate-600 text-sm font-mono cursor-pointer"
            >
              ✕
            </button>
          </div>
        )}

        {paymentError && (
          <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-xs font-bold text-rose-900 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{paymentError}</span>
            </div>
            <button onClick={() => setPaymentError(null)} className="text-rose-500 font-mono text-sm cursor-pointer">✕</button>
          </div>
        )}

        {viewParams.paymentStatus === 'success' && (viewParams.jobId ? activeJobs.some((j) => j.id === viewParams.jobId) : activeJobs.length > 0) && (
          <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in shadow-xs">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-xl text-emerald-700 shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs sm:text-sm font-black text-emerald-950">Payment Verified & Secured in Escrow!</h4>
                <p className="text-xs text-emerald-800">
                  Your professional has been hired and notified. Funds are securely held until you confirm satisfactory completion.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigateTo('messages', { jobId: viewParams.jobId, proId: viewParams.proId })}
              className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-2 shrink-0 cursor-pointer min-h-[44px]"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Open Job Chat Now</span>
            </button>
          </div>
        )}

        {/* Header Profile & Quick Action Bar */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src={currentUser.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80'}
              alt={currentUser.fullName || 'Customer'}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover ring-2 ring-emerald-100 shrink-0"
            />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900">
                  {currentUser.fullName}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200/60">
                  Customer
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>{currentUser.address || `${currentUser.lga || 'Ikeja'}, ${currentUser.state || 'Lagos'}, Nigeria`}</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => setShowProfileModal(true)}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200/60 transition-colors flex items-center gap-1.5 cursor-pointer min-h-[44px]"
            >
              <User className="w-3.5 h-3.5 text-slate-500" />
              <span>Edit Profile</span>
            </button>
            <TelegramChatButton variant="inline" customText="Telegram Bot AI" />
            <button
              id="dashboard-post-job-btn"
              onClick={() => navigateTo('post_job')}
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer min-h-[44px]"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Post a New Job</span>
            </button>
          </div>
        </div>

        {/* Customer Quick Access Hub: Job Posting, Quotes, Active Jobs & Tracking, Payments, Messages */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
          <button
            type="button"
            onClick={() => navigateTo('post_job')}
            className="p-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-left shadow-sm transition-all flex flex-col justify-between gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
              <PlusCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-extrabold text-xs block">Post a Job</span>
              <span className="text-[10px] text-emerald-100 block">Pay only when you hire</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('quotes')}
            className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
              activeTab === 'quotes'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <FileText className="w-4 h-4" />
              </div>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-amber-100 text-amber-900">
                {customerQuotes.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Quotes</span>
              <span className={`text-[10px] block ${activeTab === 'quotes' ? 'text-slate-300' : 'text-slate-500'}`}>
                Compare & accept
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
              activeTab === 'active'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Clock className="w-4 h-4" />
              </div>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-blue-100 text-blue-900">
                {activeCardJobs.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Active & Tracking</span>
              <span className={`text-[10px] block ${activeTab === 'active' ? 'text-slate-300' : 'text-slate-500'}`}>
                Live job tracking
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
              activeTab === 'payments'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CreditCard className="w-4 h-4" />
              </div>
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <span className="font-extrabold text-xs block">Payments & Escrow</span>
              <span className={`text-[10px] block ${activeTab === 'payments' ? 'text-slate-300' : 'text-slate-500'}`}>
                Paystack protection
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigateTo('messages')}
            className="p-3.5 rounded-2xl border border-slate-200/80 bg-white hover:border-emerald-400 text-slate-900 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <span className="font-extrabold text-xs block">Messages</span>
              <span className="text-[10px] text-slate-500 block">Chat with artisans</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('jobs')}
            className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
              activeTab === 'jobs'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-slate-100 text-slate-800">
                {customerJobs.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">All My Jobs</span>
              <span className={`text-[10px] block ${activeTab === 'jobs' ? 'text-slate-300' : 'text-slate-500'}`}>
                {completedJobs.length} completed
              </span>
            </div>
          </button>
        </div>

        {/* Action Required Banner: Accepted Jobs Awaiting Customer Escrow Payment */}
        {(() => {
          const unpaidAssignedJobs = customerJobs.filter((j) => {
            if (isTrulyCompletedJob(j) || isJobCancelledOrExpired(j)) return false;
            if (isJobFundedInEscrowOrReleased(j)) return false;
            const pState = paymentStatusesByJobId[j.id];
            const rawPay = String(
              j.paymentStatus ||
                (j as any).payment_status ||
                (j.aiScopeEstimate as any)?.lifecycle_meta?.payment_status ||
                pState?.paymentStatus ||
                ''
            ).toLowerCase();
            const hasAcceptedQ = quotes.some((q) => q.jobId === j.id && q.status === 'accepted');
            return j.status === 'assigned' || rawPay === 'awaiting_payment' || (j.status === 'open' && hasAcceptedQ);
          });

          if (unpaidAssignedJobs.length === 0) return null;

          return (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-3xl p-5 sm:p-6 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-amber-900">
                <Clock className="w-4 h-4 text-amber-700 shrink-0" />
                <span className="text-xs font-black uppercase tracking-wider">
                  Action Required: Complete Paystack Escrow Payment to Start Work
                </span>
              </div>
              <div className="space-y-2.5">
                {unpaidAssignedJobs.map((job) => {
                  const accQuote =
                    quotes.find((q) => q.jobId === job.id && q.status === 'accepted') ||
                    quotes.find((q) => q.jobId === job.id);
                  const amount = Number(accQuote?.totalAmount || job.grossAmount || job.budgetEstimate || 15000);
                  return (
                    <div
                      key={`unpaid-${job.id}`}
                      className="bg-white rounded-2xl p-4 border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div>
                        <h3 className="text-sm font-black text-slate-900">{job.title}</h3>
                        <p className="text-xs text-slate-600 mt-0.5">
                          {accQuote?.professionalName || 'Your professional'} accepted this job at{' '}
                          <strong className="text-slate-900">₦{amount.toLocaleString()}</strong>. Work will start immediately after your escrow payment is verified.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            navigateTo('job_details', {
                              jobId: job.id,
                              quoteId: accQuote?.id,
                              pay: 'true',
                            })
                          }
                          className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs flex items-center gap-1.5 cursor-pointer"
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                          <span>Pay ₦{amount.toLocaleString()} Escrow</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Action Required Banner for Awaiting Customer Confirmation */}
        {awaitingConfirmationJobs.length > 0 && (
          <div className="bg-gradient-to-r from-purple-900 to-indigo-950 text-white rounded-3xl p-6 sm:p-7 shadow-lg border border-purple-800 space-y-4">
            <div className="flex items-center gap-2.5 text-purple-300">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping"></span>
              <span className="text-xs font-black uppercase tracking-wider">Action Required: Physical Inspection & Completion Confirmation</span>
            </div>
            <div className="space-y-3">
              {awaitingConfirmationJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white/10 backdrop-blur-xs rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-white/15"
                >
                  <div className="space-y-1">
                    <h3 className="text-base font-black text-white">{job.title}</h3>
                    <p className="text-xs text-purple-200">
                      The assigned professional has marked this work as complete with photo proof. Please inspect the finished work and confirm completion to release escrow funds.
                    </p>
                  </div>
                  {canReleaseJobPayment(job) && (
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmingJobId(job.id);
                        setHasAuthorizedRelease(false);
                      }}
                      className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer min-h-[44px]"
                    >
                      <CheckCircle2 className="w-4 h-4 text-slate-950" />
                      <span>Release Payment</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dashboard Navigation Tabs */}
        <div className="flex items-center gap-1.5 p-1.5 bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-x-auto scrollbar-none">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Overview</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('quotes')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'quotes'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Quotes</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'quotes' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {customerQuotes.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'active'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Active Jobs & Tracking</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'active' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {activeCardJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'payments'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <CreditCard className="w-3.5 h-3.5" />
            <span>Payments & Escrow</span>
          </button>
          <button
            type="button"
            onClick={() => navigateTo('messages')}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Messages</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('jobs')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'jobs'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>My Jobs</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'jobs' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {customerJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'completed'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Completed</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'completed' ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700 font-extrabold'}`}>
              {completedJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'notifications'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Bell className="w-3.5 h-3.5" />
            <span>Notifications</span>
            {unreadNotifsCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('help')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'help'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Help & Support</span>
          </button>
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="px-4 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ml-auto"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout</span>
          </button>
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-fade-in">
            {/* Interactive 4 Metric Command Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                type="button"
                onClick={() => {
                  setJobStatusFilter('all');
                  setActiveTab('jobs');
                }}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-slate-500 text-xs font-bold">
                  <span>Total Requests</span>
                  <div className="w-8 h-8 rounded-xl bg-slate-100 group-hover:bg-emerald-50 flex items-center justify-center transition-colors">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-slate-900 tabular-nums">{customerJobs.length}</p>
                <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                  <span>Manage all requests</span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setJobStatusFilter('open');
                  setActiveTab('jobs');
                }}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-amber-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-amber-600 text-xs font-bold">
                  <span>Awaiting Quotes</span>
                  <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-amber-600 tabular-nums">{openJobs.length}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span>{customerQuotes.filter((q) => q.status === 'pending').length} pending quote(s)</span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveSubFilter('all');
                  setActiveTab('active');
                }}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-blue-600 text-xs font-bold">
                  <span>Active & In Progress</span>
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-blue-600 tabular-nums">{activeCardJobs.length}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span>
                    {awaitingConfirmationJobs.length > 0
                      ? `${awaitingConfirmationJobs.length} awaiting your confirmation`
                      : 'Live escrow execution'}
                  </span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('completed')}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-emerald-600 text-xs font-bold">
                  <span>Completed Jobs</span>
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-emerald-600 tabular-nums">{completedJobs.length}</p>
                <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                  <span>View history & rate artisans</span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>
            </div>

            {/* Interactive 4-Stage Workflow Pipeline Bar */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">
                    Interactive Service Lifecycle
                  </span>
                  <h2 className="text-sm sm:text-base font-black text-slate-900">
                    Click Any Stage to Filter Your Jobs
                  </h2>
                </div>
                <span className="text-xs text-slate-500">
                  Completed jobs automatically move out of Active & In Progress into Completed History
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setJobStatusFilter('open');
                    setActiveTab('jobs');
                  }}
                  className="p-3.5 rounded-2xl border border-amber-200/80 bg-amber-50/50 hover:bg-amber-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-amber-700 block">Stage 1 • Open</span>
                    <p className="text-xs font-black text-slate-900">Receiving Quotes</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-amber-500 text-white font-black text-xs tabular-nums">
                    {openJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveSubFilter('in_progress');
                    setActiveTab('active');
                  }}
                  className="p-3.5 rounded-2xl border border-blue-200/80 bg-blue-50/50 hover:bg-blue-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-blue-700 block">Stage 2 • Active</span>
                    <p className="text-xs font-black text-slate-900">Artisan Working</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-blue-600 text-white font-black text-xs tabular-nums">
                    {activeJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveSubFilter('awaiting');
                    setActiveTab('active');
                  }}
                  className="p-3.5 rounded-2xl border border-purple-200/80 bg-purple-50/50 hover:bg-purple-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-purple-700 block">Stage 3 • Inspect</span>
                    <p className="text-xs font-black text-slate-900">Confirm & Release</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-purple-600 text-white font-black text-xs tabular-nums">
                    {awaitingConfirmationJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('completed')}
                  className="p-3.5 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 hover:bg-emerald-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-emerald-700 block">Stage 4 • Done</span>
                    <p className="text-xs font-black text-slate-900">Completed & Paid</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-emerald-600 text-white font-black text-xs tabular-nums">
                    {completedJobs.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Quick Actions & Recent Jobs Strip */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left 2 Cols: Active & Pending Jobs (Strictly Excludes Completed Jobs) */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base sm:text-lg font-black text-slate-900">Active & Pending Service Activity</h2>
                    <p className="text-[11px] text-slate-500">
                      Ongoing jobs and open quote requests only — completed jobs move to Completed History
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('active')}
                    className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer"
                  >
                    <span>Active Tab ({activeCardJobs.length})</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {(() => {
                  const ongoingAndOpenJobs = customerJobs.filter(
                    (j) => !isTrulyCompletedJob(j) && String(j.status).toLowerCase() !== 'cancelled'
                  );

                  if (customerJobs.length === 0) {
                    return (
                      <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-slate-200/80 space-y-3">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                          <PlusCircle className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-black text-slate-900">You haven't posted any jobs yet</h3>
                        <p className="text-xs text-slate-500 max-w-sm mx-auto">
                          Describe your problem in plain words. Our AI will automatically categorize your job and match you with verified artisans across Nigeria.
                        </p>
                        <button
                          onClick={() => navigateTo('post_job')}
                          className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md transition-all cursor-pointer inline-flex items-center gap-2"
                        >
                          <PlusCircle className="w-4 h-4" />
                          <span>Post Your First Job</span>
                        </button>
                      </div>
                    );
                  }

                  if (ongoingAndOpenJobs.length === 0) {
                    return (
                      <div className="bg-white rounded-3xl p-8 text-center border border-slate-200/80 space-y-3">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                          <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-black text-slate-900">All your jobs are completed!</h3>
                        <p className="text-xs text-slate-500 max-w-md mx-auto">
                          You have no active or pending jobs right now. Your {completedJobs.length} completed job(s) are safely archived in your Completed tab.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-2.5 pt-1">
                          <button
                            type="button"
                            onClick={() => setActiveTab('completed')}
                            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs cursor-pointer inline-flex items-center gap-1.5"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            <span>View Completed Jobs ({completedJobs.length})</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => navigateTo('post_job')}
                            className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                          >
                            <PlusCircle className="w-3.5 h-3.5" />
                            <span>Post a New Job</span>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {ongoingAndOpenJobs.slice(0, 4).map((job) => {
                        const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                        const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                        const isAwaiting = awaitingConfirmationJobs.some((aj) => aj.id === job.id);

                        return (
                          <div
                            key={job.id}
                            className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs hover:border-slate-300 transition-all space-y-3"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                                  <CategoryIcon slug={job.categorySlug} className="w-5 h-5" />
                                </div>
                                <div>
                                  <h3 className="text-sm sm:text-base font-black text-slate-900">{job.title}</h3>
                                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                    <span>{job.categoryName}</span>
                                    <span>•</span>
                                    <span>{job.lga}, {job.state}</span>
                                    <span>•</span>
                                    <span>{new Date(job.createdAt).toLocaleDateString()}</span>
                                  </p>
                                </div>
                              </div>
                              <span
                                className={`px-3 py-1 rounded-full text-[11px] font-extrabold w-fit ${
                                  isAwaiting
                                    ? 'bg-purple-100 text-purple-900 border border-purple-300 animate-pulse'
                                    : job.status === 'open'
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : 'bg-blue-50 text-blue-800 border border-blue-200'
                                }`}
                              >
                                {isAwaiting
                                  ? 'Needs Your Confirmation'
                                  : job.status === 'open'
                                  ? 'Open for Quotes'
                                  : 'In Progress'}
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 text-xs">
                              <span className="text-slate-600 font-semibold">
                                Quotes: <strong className="text-emerald-700">{jobQuotes.length}</strong>
                                {acceptedQuote && ` (Hired: ${acceptedQuote.professionalName})`}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedJobForDetails(job)}
                                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                                >
                                  Details
                                </button>
                                <ShareJobButton job={job} variant="compact" showDropdown={false} />
                                {jobQuotes.length > 0 && job.status === 'open' && (
                                  <button
                                    type="button"
                                    onClick={() => navigateTo('quote_comparison', { jobId: job.id })}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1"
                                  >
                                    <span>Compare Quotes</span>
                                    <ArrowRight className="w-3 h-3" />
                                  </button>
                                )}
                                {canReleaseJobPayment(job) ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setConfirmingJobId(job.id);
                                      setHasAuthorizedRelease(false);
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1 shadow-xs"
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>Release Payment</span>
                                  </button>
                                ) : isAwaiting ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setConfirmingJobId(job.id);
                                      setHasAuthorizedRelease(false);
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1"
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>Inspect & Confirm</span>
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Right Col: Trust & Quick Navigation Cards */}
              <div className="space-y-4">
                <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-black text-sm">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                    <span>100% Escrow Protection</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    When you hire through JobMatch NG, your payment is held safely in escrow. The professional only receives their 90% net earnings when you physically inspect and confirm the job is completed.
                  </p>
                  <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
                    <button
                      onClick={() => navigateTo('customer_guide')}
                      className="text-left text-xs font-bold text-emerald-700 hover:underline flex items-center justify-between"
                    >
                      <span>Read Customer Guide</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => navigateTo('verification')}
                      className="text-left text-xs font-bold text-emerald-700 hover:underline flex items-center justify-between"
                    >
                      <span>Our 4-Step Artisan Verification</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-6 shadow-md space-y-3">
                  <h3 className="text-sm font-black text-white">Need an Emergency Repair?</h3>
                  <p className="text-xs text-slate-300">
                    Plumbing leak, generator fault, or electrical sparking? Post a job with "Emergency" priority for immediate local response.
                  </p>
                  <button
                    onClick={() => navigateTo('post_job')}
                    className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md transition-all cursor-pointer"
                  >
                    Post Emergency Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: MY JOBS (Full Searchable & Filterable List) */}
        {activeTab === 'jobs' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Filters Bar */}
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  placeholder="Search your requests by title, description, or LGA..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500"
                />
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-1 sm:pb-0">
                {(['all', 'open', 'in_progress', 'completed_by_professional', 'completed'] as const).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setJobStatusFilter(st)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      jobStatusFilter === st
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {st === 'all'
                      ? 'All'
                      : st === 'open'
                      ? 'Open'
                      : st === 'in_progress'
                      ? 'In Progress'
                      : st === 'completed_by_professional'
                      ? 'Needs Review'
                      : 'Completed'}
                  </button>
                ))}
              </div>
            </div>

            {/* Jobs List */}
            {filteredCustomerJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <FileText className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No matching requests found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Try adjusting your search query or status filter.
                </p>
                {jobSearch && (
                  <button
                    onClick={() => {
                      setJobSearch('');
                      setJobStatusFilter('all');
                    }}
                    className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold cursor-pointer"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCustomerJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const isDone = isTrulyCompletedJob(job);
                  const isAwaiting = !isDone && awaitingConfirmationJobs.some((aj) => aj.id === job.id);

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs hover:border-slate-300 transition-all space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                            <CategoryIcon slug={job.categorySlug} className="w-6 h-6" />
                          </div>
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base sm:text-lg font-black text-slate-900">{job.title}</h3>
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                  isDone
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                    : isAwaiting
                                    ? 'bg-purple-100 text-purple-900 border border-purple-300 animate-pulse'
                                    : job.status === 'open'
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : 'bg-blue-50 text-blue-800 border border-blue-200'
                                }`}
                              >
                                {isDone
                                  ? 'Completed'
                                  : isAwaiting
                                  ? 'Action Required: Confirm Completion'
                                  : job.status === 'open'
                                  ? 'Open for Quotes'
                                  : 'In Progress'}
                              </span>
                              {job.urgency === 'emergency' && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200">
                                  Emergency
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                              <span>{job.categoryName}</span>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-slate-400" />
                                {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga}, {job.state}
                              </span>
                              <span>•</span>
                              <span>Posted {new Date(job.createdAt).toLocaleDateString()}</span>
                            </p>
                          </div>
                        </div>

                        <div className="sm:text-right shrink-0">
                          <span className="text-[10px] uppercase font-bold text-slate-400 block">Estimated Budget</span>
                          <span className="text-lg font-black text-slate-900">₦{(job.budgetEstimate || 0).toLocaleString()}</span>
                        </div>
                      </div>

                      <p className="text-xs text-slate-600 leading-relaxed">{job.description}</p>

                      {/* Diagnostic tags */}
                      {job.aiAnalysis && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200/60 text-[10px] font-bold flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-emerald-600" />
                            AI Diagnosis: {job.aiAnalysis.detected_category} ({job.aiAnalysis.estimated_job_complexity})
                          </span>
                          {job.aiAnalysis.required_skills?.map((sk, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px]">
                              {sk}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Real-Time Job Status Tracker Component */}
                      <RealTimeJobStatusTracker job={job} quotes={jobQuotes} compact={true} onRefresh={refreshJobsAndQuotes} onRateJob={(target) => setRatingJob(target)} />

                      {/* Action Bar */}
                      <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-700">
                            Quotes: <strong className="text-emerald-700">{jobQuotes.length}</strong>
                          </span>
                          {acceptedQuote && (
                            <span className="text-slate-500">
                              (Hired: <strong>{acceptedQuote.professionalName}</strong>)
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedJobForDetails(job)}
                            className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors cursor-pointer"
                          >
                            View Details
                          </button>

                          <ShareJobButton job={job} variant="compact" showDropdown={true} />

                          {jobQuotes.length === 0 && job.status === 'open' && !isDone && (
                            <span className="px-3 py-1.5 rounded-xl bg-amber-50 text-amber-900 border border-amber-200 font-bold text-[11px] flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-amber-600" />
                              <span>No quotes yet — Matching verified artisans (Est. 15–30 mins)</span>
                            </span>
                          )}

                          {jobQuotes.length > 0 && job.status === 'open' && !isDone && (
                            <button
                              type="button"
                              onClick={() => navigateTo('quote_comparison', { jobId: job.id })}
                              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                            >
                              <span>Compare Quotes ({jobQuotes.length})</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {!isDone && job.status !== 'cancelled' && (acceptedQuote || job.selectedProfessionalId) && (
                            <button
                              type="button"
                              onClick={() => setSelectedJobForDetails(job)}
                              className="px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 font-bold text-xs transition-colors cursor-pointer"
                            >
                              {job.no_show_status === 'reported' ? 'Reassign Artisan' : 'Report No-Show'}
                            </button>
                          )}

                          {!isDone && job.status !== 'cancelled' && job.status !== 'disputed' && (
                            <button
                              type="button"
                              onClick={() => setSelectedJobForDetails(job)}
                              className="px-3.5 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs transition-colors cursor-pointer"
                            >
                              Cancel Job
                            </button>
                          )}

                          {(acceptedQuote || job.selectedProfessionalId) && (
                            <button
                              type="button"
                              onClick={() =>
                                navigateTo('messages', {
                                  jobId: job.id,
                                  proId: job.selectedProfessionalId || acceptedQuote?.professionalId,
                                })
                              }
                              className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-xs cursor-pointer flex items-center gap-1.5"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              <span>Message Pro</span>
                            </button>
                          )}

                          {!isDone && canReleaseJobPayment(job) ? (
                            <button
                              type="button"
                              data-testid="release-payment-myjobs-btn"
                              onClick={() => {
                                setConfirmingJobId(job.id);
                                setHasAuthorizedRelease(false);
                              }}
                              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md cursor-pointer flex items-center gap-1.5"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span>Release Payment</span>
                            </button>
                          ) : isDone || isPaymentReleased(job) ? (
                            <div className="px-4 py-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 font-extrabold text-xs flex items-center gap-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Payment Released & Completed</span>
                            </div>
                          ) : null}

                          {isDone && (
                            <button
                              type="button"
                              onClick={() => setRatingJob(job)}
                              className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-extrabold shadow-xs cursor-pointer flex items-center gap-1.5 transition-all"
                            >
                              <Star className="w-3.5 h-3.5 fill-white" />
                              <span>Rate Artisan ⭐</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: ACTIVE & IN PROGRESS (Strictly excludes completed jobs) */}
        {activeTab === 'active' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Active & In-Progress Service Execution</h2>
                <p className="text-xs text-slate-500">
                  Live tracking for ongoing repairs and work awaiting your confirmation. Completed jobs automatically move to the Completed tab.
                </p>
              </div>
              <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
                <button
                  type="button"
                  onClick={() => setActiveSubFilter('all')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                    activeSubFilter === 'all'
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All Active ({activeCardJobs.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSubFilter('in_progress')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                    activeSubFilter === 'in_progress'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                  }`}
                >
                  In Progress ({activeJobs.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSubFilter('awaiting')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                    activeSubFilter === 'awaiting'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-purple-800 hover:bg-purple-100'
                  }`}
                >
                  Needs Confirmation ({awaitingConfirmationJobs.length})
                </button>
              </div>
            </div>

            {displayedActiveCardJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <Clock className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No active jobs currently in progress</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  {completedJobs.length > 0
                    ? `You have ${completedJobs.length} completed job(s) archived in your Completed tab.`
                    : 'When you accept a quote and fund escrow, your active job tracking will appear here.'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                  <button
                    onClick={() => setActiveTab('jobs')}
                    className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold cursor-pointer"
                  >
                    View Open Requests
                  </button>
                  {completedJobs.length > 0 && (
                    <button
                      onClick={() => setActiveTab('completed')}
                      className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold cursor-pointer flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      <span>View Completed Jobs ({completedJobs.length})</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedActiveCardJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const proCompleted = isJobMarkedCompletedByPro(job);
                  const hasPhoto = hasJobCompletionPhoto(job);
                  const escrowHeld = isJobEscrowHeld(job);
                  const canRelease = canReleaseJobPayment(job);

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <h3 className="text-base font-black text-slate-900">{job.title}</h3>
                          <p className="text-xs text-slate-500">{job.categoryName} • {job.lga}, {job.state}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-[11px] font-extrabold w-fit ${
                          canRelease
                            ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                            : proCompleted
                            ? 'bg-purple-100 text-purple-900 border border-purple-300'
                            : 'bg-blue-50 text-blue-800 border border-blue-200'
                        }`}>
                          {canRelease
                            ? 'Work Finished • Ready for Payment Release'
                            : proCompleted
                            ? 'Professional Marked Job Completed'
                            : 'Job In Progress'}
                        </span>
                      </div>

                      {/* Real-Time Job Status Tracker Component */}
                      <RealTimeJobStatusTracker job={job} quotes={jobQuotes} onRefresh={refreshJobsAndQuotes} onRateJob={(target) => setRatingJob(target)} />

                      {/* Dynamic Work Status / Prerequisites Feedback */}
                      {!proCompleted && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50/80 border border-blue-200/80 rounded-2xl text-xs text-blue-900 font-medium">
                          <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                          <span>Artisan is currently executing the work. Escrow funds remain safely locked until completion.</span>
                        </div>
                      )}

                      {proCompleted && !hasPhoto && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 font-medium">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>The professional marked this work complete. Waiting for professional to upload completion photo evidence before payment can be released.</span>
                        </div>
                      )}

                      {proCompleted && hasPhoto && !escrowHeld && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 font-medium">
                          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>Escrow payment deposit must be confirmed before funds can be released.</span>
                        </div>
                      )}

                      {canRelease ? (
                        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-emerald-950 font-medium leading-relaxed flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span>Work finished with photo proof. Escrow funds (90%) are ready for release upon your approval.</span>
                          </div>
                          <button
                            type="button"
                            data-testid="release-payment-banner-btn"
                            onClick={() => {
                              setConfirmingJobId(job.id);
                              setHasAuthorizedRelease(false);
                            }}
                            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-sm hover:shadow transition-all flex items-center justify-center gap-1.5 cursor-pointer shrink-0 min-h-[40px]"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                            <span>Release Payment</span>
                          </button>
                        </div>
                      ) : null}

                      {(() => {
                        const effectiveProId = job.selectedProfessionalId || acceptedQuote?.professionalId;
                        const assignedPro = profiles.find((p) => p.id === effectiveProId);
                        const proName =
                          acceptedQuote?.professionalName ||
                          job.selectedProfessionalName ||
                          assignedPro?.fullName ||
                          assignedPro?.businessName ||
                          (effectiveProId ? 'Assigned Professional' : null);
                        const proPhone =
                          cleanContactPhone(assignedPro?.phone) ||
                          cleanContactPhone(acceptedQuote?.professionalPhone) ||
                          cleanContactPhone(job.selectedProfessionalPhone);
                        const agreedPrice = acceptedQuote?.totalAmount || job.budgetEstimate;

                        if (!proName && !effectiveProId) return null;

                        return (
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                                {(proName || 'P').charAt(0)}
                              </div>
                              <div>
                                <h4 className="text-xs font-extrabold text-slate-900">{proName || 'Assigned Professional'}</h4>
                                <p className="text-[11px] text-slate-500">
                                  {agreedPrice ? `Agreed Amount: ₦${agreedPrice.toLocaleString()} • ` : ''}100% Escrow Funded
                                  {proPhone ? ` • ${proPhone}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {proPhone && (
                                <a
                                  href={`tel:${proPhone}`}
                                  className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold flex items-center gap-1 min-h-[38px]"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>Call {proPhone}</span>
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => navigateTo('messages', { jobId: job.id, proId: effectiveProId })}
                                className="px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer min-h-[38px]"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                <span>Open Chat with Professional</span>
                              </button>
                              {canRelease ? (
                                <button
                                  type="button"
                                  data-testid="release-payment-chatbar-btn"
                                  onClick={() => {
                                    setConfirmingJobId(job.id);
                                    setHasAuthorizedRelease(false);
                                  }}
                                  className="px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black flex items-center justify-center gap-1.5 shadow-sm hover:shadow transition-all cursor-pointer min-h-[38px]"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                  <span>Release Payment</span>
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => setSelectedJobForDetails(job)}
                          className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold cursor-pointer"
                        >
                          View Details
                        </button>
                        {canRelease ? (
                          <button
                            type="button"
                            data-testid="release-payment-btn"
                            onClick={() => {
                              setConfirmingJobId(job.id);
                              setHasAuthorizedRelease(false);
                            }}
                            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold shadow-xs cursor-pointer flex items-center gap-1.5"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Release Payment</span>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 3B: COMPLETED JOBS HISTORY */}
        {activeTab === 'completed' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Completed Service History</h2>
                <p className="text-xs text-slate-500">
                  All jobs where workmanship has been verified and escrow funds have been settled.
                </p>
              </div>
              <span className="px-3.5 py-1.5 rounded-2xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-extrabold w-fit">
                {completedJobs.length} Completed Job{completedJobs.length === 1 ? '' : 's'}
              </span>
            </div>

            {completedJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <CheckCircle2 className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No completed jobs yet</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Once you confirm an artisan's completed work and release escrow payment, the finished job will be archived here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const effectiveProId = job.selectedProfessionalId || acceptedQuote?.professionalId;
                  const assignedPro = profiles.find((p) => p.id === effectiveProId);
                  const proName = acceptedQuote?.professionalName || assignedPro?.fullName || assignedPro?.businessName || job.selectedProfessionalName || 'Verified Artisan';
                  const settledAmount = acceptedQuote?.totalAmount || job.budgetEstimate || 0;

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-emerald-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3.5">
                          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                            <CategoryIcon slug={job.categorySlug} className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base font-black text-slate-900">{job.title}</h3>
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-900 border border-emerald-300">
                                Completed & Settled
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {job.categoryName} • {job.lga}, {job.state} • Artisan: <strong>{proName}</strong>
                            </p>
                          </div>
                        </div>
                        <div className="sm:text-right">
                          <span className="text-[10px] font-bold uppercase text-slate-400 block">Settled Escrow</span>
                          <span className="text-base font-black text-emerald-700 tabular-nums">₦{settledAmount.toLocaleString()}</span>
                        </div>
                      </div>

                      <RealTimeJobStatusTracker
                        job={job}
                        quotes={jobQuotes}
                        compact={true}
                        onRefresh={refreshJobsAndQuotes}
                        onRateJob={(target) => setRatingJob(target)}
                      />

                      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 text-xs text-emerald-800 font-bold">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>90% Artisan Payout Disbursed • 10% Platform Fee Settled</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedJobForDetails(job)}
                            className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer"
                          >
                            View Full Dossier
                          </button>
                          <button
                            type="button"
                            onClick={() => setRatingJob(job)}
                            className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-extrabold shadow-xs cursor-pointer flex items-center gap-1.5 transition-all"
                          >
                            <Star className="w-3.5 h-3.5 fill-white" />
                            <span>Rate Artisan ⭐</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 4: QUOTES RECEIVED */}
        {activeTab === 'quotes' && (
          <div className="space-y-4 animate-fade-in">
            <h2 className="text-base sm:text-lg font-black text-slate-900">Quotes & Offers Received</h2>
            {customerQuotes.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <DollarSign className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No quotes received yet</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  When verified professionals view your requests and submit quotes, they will appear here.
                </p>
                <button
                  onClick={() => navigateTo('post_job')}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold cursor-pointer"
                >
                  Post a Job Request
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {customerQuotes.map((q) => {
                  const parentJob = jobs.find((j) => j.id === q.jobId);
                  return (
                    <div
                      key={q.id}
                      className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-extrabold text-slate-900">{q.professionalName}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            q.status === 'accepted'
                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              : 'bg-amber-50 text-amber-800 border border-amber-200'
                          }`}>
                            {q.status === 'accepted' ? 'Accepted / Hired' : 'Pending Review'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          Job: <strong>{parentJob?.title || 'Service Request'}</strong> • Submitted: {new Date(q.createdAt).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-600 italic">"{q.notes || 'Itemized quote ready for inspection.'}"</p>
                      </div>

                      <div className="flex flex-col sm:items-end gap-2 shrink-0">
                        <span className="text-base font-black text-slate-900">₦{q.totalAmount.toLocaleString()}</span>
                        <div className="flex items-center gap-2">
                          {parentJob && isJobFundedInEscrowOrReleased(parentJob) ? (
                            <button
                              type="button"
                              onClick={() => setActiveTab('active')}
                              className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1"
                            >
                              <ShieldCheck className="w-3.5 h-3.5" />
                              <span>Escrow Funded • View Active Job</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => navigateTo('quote_comparison', { jobId: q.jobId })}
                              className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1"
                            >
                              <span>Compare & Accept</span>
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 5: ESCROW & PAYMENTS */}
        {activeTab === 'payments' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-black text-slate-900">100% Escrow Payment Protection</h2>
                  <p className="text-xs text-slate-500">Your funds remain safely secured with Paystack until you verify completed workmanship.</p>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 text-xs text-slate-700 leading-relaxed space-y-2">
                <h4 className="font-extrabold text-slate-900">How JobMatch NG Payments Work:</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Step 1:</strong> You accept an artisan quote and pay 100% via Paystack (card, bank transfer, USSD).</li>
                  <li><strong>Step 2:</strong> Funds are held in escrow. The job status updates to <em>In Progress</em>.</li>
                  <li><strong>Step 3:</strong> The artisan completes work and uploads a temporary completion photo proof.</li>
                  <li><strong>Step 4:</strong> You inspect the physical work and click <em>Confirm Work Completed</em>.</li>
                  <li><strong>Step 5:</strong> The system automatically releases 90% net earnings to the professional and records JobMatch 10% platform commission.</li>
                </ul>
              </div>
            </div>

            {/* List of Payments for customer jobs */}
            <div className="space-y-3">
              <h3 className="text-sm font-black text-slate-900">Your Job Payment Records</h3>
              {customerJobs.length === 0 ? (
                <p className="text-xs text-slate-500">No payment records found.</p>
              ) : (
                customerJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="space-y-1">
                        <h4 className="text-sm font-black text-slate-900">{job.title}</h4>
                        <p className="text-xs text-slate-500">{job.categoryName} • Budget: ₦{(job.budgetEstimate || 0).toLocaleString()}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-[11px] font-bold ${
                          isTrulyCompletedJob(job)
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : isJobFundedInEscrowOrReleased(job)
                            ? 'bg-blue-50 text-blue-800 border border-blue-200'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {isTrulyCompletedJob(job)
                            ? 'Escrow Released ✅'
                            : isJobFundedInEscrowOrReleased(job)
                            ? 'Held in Escrow 🔒'
                            : 'Open / Unfunded'}
                        </span>
                        {!isTrulyCompletedJob(job) && !isJobFundedInEscrowOrReleased(job) && acceptedQuote && (
                          <button
                            type="button"
                            onClick={() => handleInitiatePayment(job, acceptedQuote)}
                            disabled={initiatingPayJobId === job.id}
                            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs cursor-pointer disabled:opacity-50"
                          >
                            {initiatingPayJobId === job.id ? 'Connecting Paystack...' : 'Fund Escrow'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* TAB 6: NOTIFICATIONS */}
        {activeTab === 'notifications' && (
          <div className="space-y-6 animate-fade-in">
            {/* Browser Push Notifications Card */}
            {currentUser?.id && (
              <PushNotificationSettingsCard
                userId={currentUser.id}
                userRole="customer"
                pushEnabledPreference={notifPrefs?.push_enabled}
                onPushPreferenceChange={(enabled) => {
                  setNotifPrefs((prev: any) => (prev ? { ...prev, push_enabled: enabled } : prev));
                }}
              />
            )}

            {/* Customer Notification Preferences Card */}
            {notifPrefs && (
              <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm sm:text-base font-black text-slate-900">Notification Channels & Alert Preferences</h3>
                    <p className="text-xs text-slate-500">
                      Choose how JobMatch NG notifies you about quotes, job progress, escrow payments, and account security.
                    </p>
                  </div>
                  {notifPrefsSavedMsg && (
                    <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-bold">
                      {notifPrefsSavedMsg}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  {(
                    [
                      { key: 'in_app_enabled', label: 'In-App Notifications', desc: 'Instant dashboard & bell alerts' },
                      { key: 'email_enabled', label: 'Email Notifications', desc: 'Quotes, job updates & receipts' },
                      { key: 'sms_enabled', label: 'SMS Alerts', desc: 'Critical security & escrow events' },
                    ] as const
                  ).map((item) => (
                    <label
                      key={item.key}
                      className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 cursor-pointer"
                    >
                      <div>
                        <span className="text-xs font-extrabold text-slate-900 block">{item.label}</span>
                        <span className="text-[11px] text-slate-500">{item.desc}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={Boolean(notifPrefs[item.key])}
                        disabled={savingNotifPrefs}
                        onChange={() => handleToggleNotifPref(item.key)}
                        className="w-4 h-4 accent-emerald-600 rounded cursor-pointer"
                      />
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 pt-2 border-t border-slate-100 text-xs">
                  {(
                    [
                      { key: 'quote_received', label: 'New Quote Received' },
                      { key: 'job_accepted', label: 'Job Accepted & Reassignment' },
                      { key: 'job_completed', label: 'Artisan Marked Job Completed' },
                      { key: 'payment_escrow_updates', label: 'Escrow Released / Refunded' },
                      { key: 'security_alerts', label: 'Account & Profile Security Alerts' },
                      { key: 'login_alerts', label: 'New Login Activity Alerts' },
                    ] as const
                  ).map((ev) => (
                    <label key={ev.key} className="flex items-center gap-2 text-slate-700 font-semibold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(notifPrefs[ev.key])}
                        disabled={savingNotifPrefs}
                        onChange={() => handleToggleNotifPref(ev.key)}
                        className="w-3.5 h-3.5 accent-emerald-600 rounded cursor-pointer"
                      />
                      <span>{ev.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-black text-slate-900">Recent Notifications</h2>
              <span className="text-xs text-slate-500">{customerNotifications.length} total</span>
            </div>

            {customerNotifications.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-2">
                <Bell className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No notifications</h3>
                <p className="text-xs text-slate-500">You're all caught up with your service updates.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {customerNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => {
                      if (!notif.read) markNotificationAsRead(notif.id);
                    }}
                    className={`p-4 sm:p-5 rounded-3xl border transition-all cursor-pointer flex items-start gap-3 ${
                      notif.read ? 'bg-white border-slate-200/80' : 'bg-emerald-50/50 border-emerald-200 shadow-xs'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell className="w-4 h-4" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-black text-slate-900">{notif.title}</h4>
                        <span className="text-[10px] text-slate-400">{new Date(notif.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-slate-600">{notif.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 7: HELP & SUPPORT */}
        {activeTab === 'help' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-black text-slate-900">Customer Support & FAQs</h2>
                  <p className="text-xs text-slate-500">Get quick answers or speak directly with our team.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-900">Telegram AI Assistant</h4>
                  <p className="text-xs text-slate-600">
                    Need instant answers on WhatsApp or Telegram? Connect directly with <strong>@JobMatchNG_AIAssistant_bot</strong> for 24/7 AI-assisted troubleshooting.
                  </p>
                  <TelegramChatButton variant="inline" customText="Launch Telegram Bot" />
                </div>

                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-900">Customer Safety & Disputes</h4>
                  <p className="text-xs text-slate-600">
                    If an artisan fails to show up or delivers substandard work, your escrow payment is protected. Do not confirm completion until work is fully resolved.
                  </p>
                  <button
                    onClick={() => navigateTo('contact')}
                    className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs cursor-pointer"
                  >
                    Contact Support Team
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Release Confirmation Modal */}
      {confirmingJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 space-y-5 my-8">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">Payment Release Confirmation</h3>
                  <p className="text-[11px] text-slate-500">Review completed workmanship & authorize 90% artisan payout</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConfirmingJobId(null)}
                className="text-slate-400 hover:text-slate-600 font-bold cursor-pointer p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {(() => {
              const targetJob = jobs.find((j) => j.id === confirmingJobId);
              const targetQuote = quotes.find((q) => q.jobId === confirmingJobId && q.status === 'accepted') || quotes.find((q) => q.jobId === confirmingJobId);
              const grossAmount = targetQuote?.totalAmount || targetJob?.budgetEstimate || 10000;
              const proShare = Math.round(grossAmount * 0.9);
              const platformShare = grossAmount - proShare;
              const displayPhoto = tempModalPhoto || completionPhotosByJobId[confirmingJobId] || targetJob?.completionPhotoUrl;
              const displayNotes = targetJob?.completionNotes;
              const assignedPro = profiles.find((p) => p.id === (targetJob?.selectedProfessionalId || targetQuote?.professionalId));
              const proName = targetQuote?.professionalName || assignedPro?.fullName || assignedPro?.businessName || 'Assigned Artisan';

              return (
                <div className="space-y-4">
                  {/* Job & Professional Brief Header */}
                  <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/60 flex items-center justify-between text-xs">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">Job Request</span>
                      <strong className="text-slate-900 font-black">{targetJob?.title || 'Service Request'}</strong>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">Professional</span>
                      <strong className="text-emerald-800 font-extrabold">{proName}</strong>
                    </div>
                  </div>

                  {/* 90% Escrow Payout Breakdown Card */}
                  <div className="p-4 bg-emerald-50/60 border border-emerald-200/80 rounded-2xl space-y-3">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <span className="text-[10px] uppercase font-black text-emerald-800 tracking-wider block">
                          90% Professional Net Payout
                        </span>
                        <div className="text-2xl sm:text-3xl font-black text-emerald-700">
                          ₦{proShare.toLocaleString()}
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-900 border border-emerald-300">
                        Escrow Protected
                      </span>
                    </div>

                    <div className="pt-2 border-t border-emerald-200/70 space-y-1 text-xs text-slate-600">
                      <div className="flex items-center justify-between">
                        <span>Total Escrow Deposit (100%):</span>
                        <span className="font-bold text-slate-800">₦{grossAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span>Platform Commission (10%):</span>
                        <span>₦{platformShare.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Professional's Completion Photo Evidence */}
                  <div className="p-3.5 bg-purple-50/80 border border-purple-200 rounded-2xl space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <div>
                        <span className="text-xs font-black text-purple-950 flex items-center gap-1.5">
                          <span>📷 Temporary Work Completion Photo Evidence</span>
                        </span>
                        <span className="text-[10px] text-purple-700 block font-medium">
                          (Provided for your review prior to payment release. Automatically removed after confirmation to save space)
                        </span>
                      </div>
                      {displayPhoto && (
                        <button
                          type="button"
                          onClick={() => setPreviewFullscreenPhoto(displayPhoto)}
                          className="text-[11px] font-bold text-purple-700 hover:text-purple-900 underline flex items-center gap-1 cursor-pointer self-start sm:self-auto"
                        >
                          <Eye className="w-3 h-3" />
                          <span>View Full Size</span>
                        </button>
                      )}
                    </div>

                    {loadingEvidence ? (
                      <div className="p-6 bg-white rounded-xl border border-dashed border-purple-200 text-center flex flex-col items-center justify-center gap-2">
                        <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
                        <span className="text-xs text-purple-800 font-medium">Fetching completion photo evidence from server...</span>
                      </div>
                    ) : displayPhoto ? (
                      <div
                        onClick={() => setPreviewFullscreenPhoto(displayPhoto)}
                        className="rounded-xl overflow-hidden border border-purple-300 max-h-56 bg-slate-950 cursor-pointer relative group"
                        title="Click to view full size"
                      >
                        <img
                          src={displayPhoto}
                          alt="Work completion evidence uploaded by professional"
                          className="w-full h-full object-contain mx-auto max-h-56 group-hover:opacity-95 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1.5">
                          <Eye className="w-4 h-4" />
                          <span>Click to Zoom</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-white rounded-xl border border-dashed border-amber-300 text-xs text-amber-900 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span>No completion photo proof found. A verified completion photo is required from the artisan before funds can be released.</span>
                      </div>
                    )}

                    {displayNotes && (
                      <div className="p-2.5 bg-white rounded-xl border border-purple-200/60 text-[11px] text-slate-700">
                        <span className="font-bold text-purple-900 block mb-0.5">Artisan's Completion Note:</span>
                        <p className="italic">"{displayNotes}"</p>
                      </div>
                    )}
                  </div>

                  {/* Explicit Checkbox Acknowledgement */}
                  <label className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 cursor-pointer transition-all">
                    <input
                      type="checkbox"
                      checked={hasAuthorizedRelease}
                      onChange={(e) => setHasAuthorizedRelease(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer shrink-0"
                    />
                    <span className="text-xs text-slate-800 font-semibold select-none leading-relaxed">
                      I confirm that this work has been completed satisfactorily, I have reviewed the photo evidence, and I authorize the release of the 90% payment to the professional.
                    </span>
                  </label>
                </div>
              );
            })()}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Optional Feedback & Rating Notes
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="e.g., Artisan arrived on time, was courteous, and solved the fault cleanly."
                rows={2}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmingJobId(null)}
                disabled={isConfirmingCompletion}
                className="w-full sm:flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer min-h-[44px] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmWorkCompleted(confirmingJobId)}
                disabled={!hasAuthorizedRelease || isConfirmingCompletion}
                className="w-full sm:flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md cursor-pointer min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isConfirmingCompletion ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span>Processing 90% Payment Release...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span>Authorize & Release Payment (90%)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Completion Photo Preview Modal */}
      {previewFullscreenPhoto && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm animate-fade-in"
          onClick={() => setPreviewFullscreenPhoto(null)}
        >
          <div
            className="relative max-w-3xl w-full max-h-[90vh] bg-slate-900 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl flex flex-col items-center justify-center p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-4 right-4 z-10">
              <button
                type="button"
                onClick={() => setPreviewFullscreenPhoto(null)}
                className="px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer backdrop-blur-xs transition-colors"
              >
                ✕ Close Preview
              </button>
            </div>
            <img
              src={previewFullscreenPhoto}
              alt="Fullscreen completion evidence"
              className="w-full h-full object-contain max-h-[82vh] rounded-2xl"
            />
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl border border-slate-100 text-center space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <LogOut className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-slate-900">Sign Out of JobMatch NG?</h3>
            <p className="text-xs text-slate-500">
              You will need to sign back in with your credentials to access your service dashboard.
            </p>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs cursor-pointer"
              >
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Details Modal */}
      {selectedJobForDetails && (
        <JobDetailsModal
          job={selectedJobForDetails}
          isOpen={Boolean(selectedJobForDetails)}
          onClose={() => setSelectedJobForDetails(null)}
        />
      )}

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {/* 1-5 Star Artisan Rating Modal */}
      {ratingJob && (
        <JobRatingModal
          job={ratingJob}
          isOpen={Boolean(ratingJob)}
          onClose={() => setRatingJob(null)}
        />
      )}
    </div>
  );
};
